import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { QuestionAudienceSchema } from '../../questions/question-audience/question-audience';
import { AccountRefSchema } from '../../users/account-ref/account-ref';
import { InterviewMethodSchema } from '../interview/interview';

/**
 * Whether staff are offering the window. A `closed` slot keeps its bookings
 * and its place on the staff calendar; it simply stops being offered to the
 * next member — the same two states a venue's window has (P25b1).
 */
export const INTERVIEW_SLOT_STATUSES = ['open', 'closed'] as const;

export const InterviewSlotStatusSchema = z.enum(INTERVIEW_SLOT_STATUSES);

export type InterviewSlotStatus = (typeof INTERVIEW_SLOT_STATUSES)[number];

/**
 * How many interviews one window may hold at once — one per interviewer on
 * duty. Bounded so a typo cannot publish a thousand seats.
 */
export const INTERVIEW_SLOT_MAX_CAPACITY = 20;

/** A location may be a meeting URL or an address; the same bound the interview row uses. */
export const INTERVIEW_LOCATION_MAX_LENGTH = 500;

/**
 * The audience a slot is offered to. `QuestionAudienceSchema` rather than a
 * third copy of `all | male | female`: gender-matched interviewing is the
 * ordinary case (a woman interviews women), `all` is the small deployment with
 * one interviewer, and the value is data plumbing into the member's filter —
 * never a policy branch (U15).
 */
export const InterviewSlotAudienceSchema = QuestionAudienceSchema;

export type InterviewSlotAudience = z.infer<typeof InterviewSlotAudienceSchema>;

/** One interview a slot holds — what the staff calendar draws under the window. */
export const InterviewSlotBookingSchema = z
  .object({
    interviewId: UuidSchema,
    caseId: UuidSchema,
    userId: UuidSchema,
    /** `null` when the account lookup came back short; the row still stands (P14b). */
    account: AccountRefSchema.nullable(),
  })
  .strict();

export type InterviewSlotBooking = z.infer<typeof InterviewSlotBookingSchema>;

/**
 * One window staff have published for interviews (D9a) — the staff shape.
 *
 * Times are UTC instants; the calendar renders them in the operator's zone.
 * `method` is the slot's, not the member's: staff decide how they will conduct
 * the interviews in this window, and the member picks a window whose method
 * suits them. `locationOrLink` is copied onto every interview booked into the
 * window, so a video link published once reaches everybody who books it.
 *
 * `bookings` is what makes the calendar an agenda rather than a grid: who is
 * coming, and the door to their case.
 */
export const InterviewSlotSchema = z
  .object({
    id: UuidSchema,
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    audience: InterviewSlotAudienceSchema,
    method: InterviewMethodSchema,
    capacity: z.number().int(),
    status: InterviewSlotStatusSchema,
    locationOrLink: z.string().nullable(),
    /** Live interviews naming this slot — booked, not yet judged. */
    bookedCount: z.number().int().nonnegative(),
    bookings: z.array(InterviewSlotBookingSchema),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type InterviewSlot = z.infer<typeof InterviewSlotSchema>;
